import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import { billingServices } from "@services/apis/Billing";
import {
  CadTradeReportItem,
  CadTradeReportType,
} from "@services/models/Billing/CadTradeReportsModel";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConstantFunctions from "../../utils/constants_functions";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import { buildCSV, CSVColumn, downloadCSV } from "../../utils/downloadCSV";

type TradeReportMonthRow = {
  month: string;
  monthLabel: string;
  year: string;
  lastTradeDate: string | null;
  hasSummary: boolean;
  hasDetailed: boolean;
};

const constantFunctions = new ConstantFunctions();

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatMonthName(monthNumber: string): string {
  const index = Number(monthNumber) - 1;
  return MONTH_NAMES[index] ?? monthNumber;
}

function groupReportsByMonth(
  reports: CadTradeReportItem[],
): TradeReportMonthRow[] {
  const map = new Map<string, TradeReportMonthRow>();

  for (const report of reports) {
    let row = map.get(report.date);
    if (!row) {
      const [year, monthNumber] = report.date.split("-");
      row = {
        month: report.date,
        monthLabel: formatMonthName(monthNumber),
        year,
        lastTradeDate: null,
        hasSummary: false,
        hasDetailed: false,
      };
      map.set(report.date, row);
    }

    if (report.lastTradeDate) {
      const nextTime = new Date(report.lastTradeDate).getTime();
      const currentTime = row.lastTradeDate
        ? new Date(row.lastTradeDate).getTime()
        : 0;
      if (!row.lastTradeDate || nextTime > currentTime) {
        row.lastTradeDate = report.lastTradeDate;
      }
    }

    if (report.reportType === "summary") row.hasSummary = true;
    if (report.reportType === "detailed") row.hasDetailed = true;
  }

  return Array.from(map.values());
}

function rowsToCSV(rows: Record<string, unknown>[]): string {
  const headers: string[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        headers.push(key);
      }
    }
  }

  const columns: CSVColumn[] = headers.map((header) => ({
    header,
    field: header,
    formatter: (value) => {
      if (value == null) return "";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
  }));

  return buildCSV(rows, columns);
}

const TradeReports = () => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TradeReportMonthRow[]>([]);
  const [downloadLoading, setDownloadLoading] = useState<
    Record<string, boolean>
  >({});

  const gridRef = useRef<any>(null);
  const didInitialFetch = useRef(false);

  const fetchReports = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const res = await billingServices.getCadTradeReports();
      const reports = res.data?.reports ?? [];
      setData(groupReportsByMonth(reports));
    } catch (err: any) {
      const message =
        err?.response?.data?.msg ||
        err?.message ||
        "Failed to load CAD trade reports";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;
    fetchReports();
  }, [fetchReports]);

  const handleDownload = useCallback(
    async (month: string, type: CadTradeReportType) => {
      const key = `${month}-${type}`;
      if (downloadLoading[key]) return;

      setDownloadLoading((prev) => ({ ...prev, [key]: true }));

      try {
        const res = await billingServices.downloadCadTradeReport({
          month,
          type,
        });
        const rows = (res.data?.rows ?? []) as Record<string, unknown>[];
        const csv = rowsToCSV(rows);
        downloadCSV(csv, `cad-trade-report-${month}-${type}.csv`);
        handleSuccess({
          message: `${type === "summary" ? "Summary" : "Detailed"} report downloaded for ${month}`,
        });
      } catch (err: any) {
        handleError({
          message:
            err?.response?.data?.msg ||
            `Failed to download ${type} report for ${month}`,
        });
      } finally {
        setDownloadLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [downloadLoading, handleError, handleSuccess],
  );

  const renderDownloadButton = useCallback(
    (month: string, type: CadTradeReportType, available: boolean) => {
      const key = `${month}-${type}`;
      const isLoading = !!downloadLoading[key];

      return (
        <div className="flex items-center h-full">
          <button
            type="button"
            className="flex items-center gap-1 h-[24px] px-2 bg-bg-button-secondary border border-border-button-secondary rounded cursor-pointer text-[10px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleDownload(month, type)}
            disabled={!available || isLoading}>
            <span
              className={`material-symbols-outlined text-[14px] ${isLoading ? "animate-spin" : ""}`}>
              {isLoading ? "progress_activity" : "download"}
            </span>
            {isLoading ? "Downloading..." : "Download CSV"}
          </button>
        </div>
      );
    },
    [downloadLoading, handleDownload],
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Month",
        field: "monthLabel",
        flex: 1,
        minWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Year",
        field: "year",
        flex: 1,
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Last Updated Trade",
        field: "lastTradeDate",
        flex: 1,
        minWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        valueFormatter: (params) =>
          params.value
            ? constantFunctions.dateFormatInDDMMYY(params.value)
            : "—",
      },
      {
        headerName: "Summary Report",
        field: "hasSummary",
        flex: 1,
        minWidth: 180,
        sortable: false,
        filter: false,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        cellRenderer: (params: any) =>
          renderDownloadButton(
            params.data.month,
            "summary",
            params.data.hasSummary,
          ),
      },
      {
        headerName: "Detailed Report",
        field: "hasDetailed",
        flex: 1,
        minWidth: 180,
        sortable: false,
        filter: false,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        cellRenderer: (params: any) =>
          renderDownloadButton(
            params.data.month,
            "detailed",
            params.data.hasDetailed,
          ),
      },
    ],
    [renderDownloadButton],
  );

  return (
    <div
      className="flex-1 py-4 overflow-hidden"
      style={{ height: "calc(100dvh - 70px)" }}>
      {loading ? (
        <AgGridSkeleton
          columnDefs={columnDefs}
          style={{ height: "calc(100vh - 150px)" }}
        />
      ) : (
        <div className="flex flex-col h-full">
          <AgGridReactTable
            ref={gridRef}
            rowData={data}
            columnDefs={columnDefs}
            error={error ?? undefined}
            style={{ height: "calc(100vh - 150px)" }}
            getRowId={(params) => params.data.month}
          />
        </div>
      )}
    </div>
  );
};

export default TradeReports;
